import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTheme } from 'next-themes';
import { useContent } from '../contexts/ContentContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


function LoginPage() {
  const { processSessionId, loading } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t } = useContent();
  const [showRegister, setShowRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ email: '', name: '', password: '', confirmPassword: '' });
  const [forgotForm, setForgotForm] = useState({ email: '' });
  const [resetForm, setResetForm] = useState({ token: '', newPassword: '', confirmPassword: '' });
  const [siteSettings, setSiteSettings] = useState({
    site_title: 'Print Studio',
    site_logo: '',
    site_tagline: 'Professional Photo Printing'
  });

  useEffect(() => {
    // Fetch site settings
    const fetchSettings = async () => {
      try {
        const response = await axios.get('/settings');
        console.log('LoginPage - Site settings fetched:', response.data);
        setSiteSettings(response.data);
        
        // Update document title
        if (response.data.site_title) {
          document.title = response.data.site_title;
        }
        
        // Update favicon if provided
        if (response.data.favicon) {
          const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
          link.rel = 'icon';
          link.href = response.data.favicon;
          document.head.appendChild(link);
        }
      } catch (error) {
        console.error('Failed to fetch site settings:', error);
      }
    };
    fetchSettings();

    // Check for session_id in URL fragment
    const fragment = window.location.hash.substring(1);
    const params = new URLSearchParams(fragment);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      processSessionId(sessionId);
    }

    // Check for reset token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('reset_token');
    if (resetToken) {
      setResetForm(prev => ({ ...prev, token: resetToken }));
      setShowResetPassword(true);
      // Validate token
      validateResetToken(resetToken);
    }
  }, [processSessionId]);

  const handleGoogleLogin = () => {
    const redirectUrl = encodeURIComponent(`${window.location.origin}/dashboard`);
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirectUrl}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setAuthLoading(true);
    try {
      const response = await axios.post('/auth/login', loginForm);
      
      const { user, session_token } = response.data;
      
      localStorage.setItem('session_token', session_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${session_token}`;
      
      // Trigger auth context update by calling processSessionId with a mock session
      window.location.href = '/dashboard';
      
      toast.success(`Welcome back, ${user.name}!`);
      
    } catch (error) {
      console.error('Login failed:', error);
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotForm.email) {
      toast.error('Please enter your email address');
      return;
    }

    setAuthLoading(true);
    try {
      await axios.post('/auth/forgot-password', { email: forgotForm.email });
      toast.success('If an account exists, a password reset link has been sent to your email.');
      setShowForgotPassword(false);
      setForgotForm({ email: '' });
    } catch (error) {
      console.error('Forgot password failed:', error);
      toast.error('Failed to send password reset email. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleForgotUsername = async () => {
    if (!forgotForm.email) {
      toast.error('Please enter your email address');
      return;
    }

    setAuthLoading(true);
    try {
      await axios.post('/auth/forgot-username', { email: forgotForm.email });
      toast.success('If an account exists, a username reminder has been sent to your email.');
      setShowForgotPassword(false);
      setForgotForm({ email: '' });
    } catch (error) {
      console.error('Forgot username failed:', error);
      toast.error('Failed to send username reminder. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const validateResetToken = async (token) => {
    try {
      const response = await axios.get(`/auth/validate-reset-token/${token}`);
      if (!response.data.valid) {
        toast.error('Invalid or expired reset link');
        setShowResetPassword(false);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (error) {
      toast.error('Invalid reset link');
      setShowResetPassword(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetForm.newPassword || !resetForm.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (resetForm.newPassword !== resetForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (resetForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setAuthLoading(true);
    try {
      await axios.post('/auth/reset-password', {
        token: resetForm.token,
        new_password: resetForm.newPassword
      });

      toast.success('Password reset successfully! You can now sign in with your new password.');
      setShowResetPassword(false);
      setResetForm({ token: '', newPassword: '', confirmPassword: '' });
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      console.error('Password reset failed:', error);
      const message = error.response?.data?.detail || 'Password reset failed. Please try again.';
      toast.error(message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    if (!registerForm.email || !registerForm.name || !registerForm.password || !registerForm.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (registerForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setAuthLoading(true);
    try {
      const response = await axios.post('/auth/register', {
        email: registerForm.email,
        name: registerForm.name,
        password: registerForm.password
      });
      
      const { user, session_token } = response.data;
      
      localStorage.setItem('session_token', session_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${session_token}`;
      
      window.location.href = '/dashboard';
      
      toast.success(`Welcome, ${user.name}! Account created successfully.`);
      
    } catch (error) {
      console.error('Registration failed:', error);
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setAuthLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">Processing login...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50 relative">
      {/* Dark Mode Toggle - Top Right */}
      <div className="absolute top-4 right-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          className="flex items-center gap-2"
          title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {theme === 'dark' ? (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <span className="hidden sm:inline">Light</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
              <span className="hidden sm:inline">Dark</span>
            </>
          )}
        </Button>
      </div>
      
      <div className="w-full max-w-4xl mx-auto px-4">
        {/* Hero Section */}
        <div className="text-center mb-12">
          {siteSettings.site_logo && (
            <div className="flex justify-center mb-4">
              <img 
                src={siteSettings.site_logo} 
                alt="Logo" 
                className="h-20 w-auto object-contain"
                onLoad={() => console.log('Logo loaded successfully:', siteSettings.site_logo)}
                onError={(e) => console.error('Logo failed to load:', siteSettings.site_logo, e)}
              />
            </div>
          )}
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            {siteSettings.site_title || 'Print Studio'}
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            {t('hero.tagline', siteSettings.site_tagline || 'Transform your digital memories into beautiful prints. Upload, customise, and order professional-quality photos with ease.')}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('hero.feature1_title', 'Batch Upload')}</h3>
              <p className="text-gray-600">{t('hero.feature1_desc', 'Upload multiple images at once for efficient processing')}</p>
            </div>
            
            <div className="text-center">
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 011-1h1a2 2 0 100-4H7a1 1 0 01-1-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('hero.feature2_title', 'Custom Sizes')}</h3>
              <p className="text-gray-600">{t('hero.feature2_desc', 'Choose from multiple print sizes and finishes')}</p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('hero.feature3_title', 'Bulk Discounts')}</h3>
              <p className="text-gray-600">{t('hero.feature3_desc', 'Save more with volume pricing on larger orders')}</p>
            </div>
          </div>
        </div>

        {/* Login Card */}
        <Card className="w-full max-w-md mx-auto shadow-xl border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl font-bold text-gray-900">
              {showRegister ? t('register.title', 'Create Account') : t('login.title', 'Welcome Back')}
            </CardTitle>
            <CardDescription className="text-gray-600">
              {showRegister ? t('register.subtitle', 'Sign up to start creating beautiful prints') : t('login.subtitle', 'Sign in to start creating beautiful prints')}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {/* Reset Password Form */}
            {showResetPassword ? (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    id="new-password"
                    type="password"
                    value={resetForm.newPassword}
                    onChange={(e) => setResetForm(prev => ({ ...prev, newPassword: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter new password (min. 6 characters)"
                    required
                    minLength="6"
                    data-testid="new-password-input"
                  />
                </div>
                <div>
                  <label htmlFor="confirm-new-password" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <input
                    id="confirm-new-password"
                    type="password"
                    value={resetForm.confirmPassword}
                    onChange={(e) => setResetForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Confirm new password"
                    required
                    data-testid="confirm-new-password-input"
                  />
                </div>
                <Button 
                  type="submit"
                  disabled={authLoading}
                  className="w-full h-12 bg-custom-orange hover:bg-custom-orange text-white font-medium rounded-lg transition-all duration-200"
                  data-testid="reset-password-button"
                >
                  {authLoading ? 'Resetting password...' : 'Reset Password'}
                </Button>
                <div className="text-center">
                  <Button
                    variant="link"
                    onClick={() => {
                      setShowResetPassword(false);
                      window.history.replaceState({}, document.title, window.location.pathname);
                    }}
                    className="text-custom-orange hover:text-custom-orange"
                    data-testid="back-to-login"
                  >
                    Back to Sign In
                  </Button>
                </div>
              </form>
            ) : showForgotPassword ? (
              <div className="space-y-4">
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      id="forgot-email"
                      type="email"
                      value={forgotForm.email}
                      onChange={(e) => setForgotForm(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter your email address"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Button 
                      type="submit"
                      disabled={authLoading}
                      className="w-full h-12 bg-custom-orange hover:bg-custom-orange text-white font-medium rounded-lg transition-all duration-200"
                      data-testid="forgot-password-button"
                    >
                      {authLoading ? 'Sending...' : 'Send Password Reset Link'}
                    </Button>
                    <Button 
                      type="button"
                      onClick={handleForgotUsername}
                      disabled={authLoading}
                      variant="outline"
                      className="w-full h-12 border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-all duration-200"
                      data-testid="forgot-username-button"
                    >
                      Send Username Reminder
                    </Button>
                  </div>
                </form>
                <div className="text-center">
                  <Button
                    variant="link"
                    onClick={() => setShowForgotPassword(false)}
                    className="text-custom-orange hover:text-custom-orange"
                    data-testid="back-to-login-forgot"
                  >
                    Back to Sign In
                  </Button>
                </div>
              </div>
            ) : !showRegister ? (
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your email"
                    required
                    data-testid="email-input"
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your password"
                    required
                    data-testid="password-input"
                  />
                </div>
                <div className="text-right">
                  <Button
                    variant="link"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-custom-orange hover:text-custom-orange"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Button>
                </div>
                <Button
                  type="submit"
                  disabled={authLoading}
                  className="w-full h-12 bg-custom-orange hover:bg-custom-orange text-white font-medium rounded-lg transition-all duration-200"
                  data-testid="email-login-button"
                >
                  {authLoading ? (
                    <>
                      <i className="pi pi-spin pi-spinner mr-2"></i>
                      Signing in...
                    </>
                  ) : (
                    <>
                      <i className="pi pi-sign-in mr-2"></i>
                      Sign In
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleEmailRegister} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={registerForm.name}
                    onChange={(e) => setRegisterForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your full name"
                    required
                    data-testid="name-input"
                  />
                </div>
                <div>
                  <label htmlFor="register-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    id="register-email"
                    type="email"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your email"
                    required
                    data-testid="register-email-input"
                  />
                </div>
                <div>
                  <label htmlFor="register-password" className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    id="register-password"
                    type="password"
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Create a password (min. 6 characters)"
                    required
                    minLength="6"
                    data-testid="register-password-input"
                  />
                </div>
                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <input
                    id="confirm-password"
                    type="password"
                    value={registerForm.confirmPassword}
                    onChange={(e) => setRegisterForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Confirm your password"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button 
                  type="submit"
                  disabled={authLoading}
                  className="w-full h-12 bg-custom-orange hover:bg-custom-orange text-white font-medium rounded-lg transition-all duration-200"
                  data-testid="email-register-button"
                >
                  {authLoading ? (
                    <>
                      <i className="pi pi-spin pi-spinner mr-2"></i>
                      Creating account...
                    </>
                  ) : (
                    <>
                      <i className="pi pi-user-plus mr-2"></i>
                      Create Account
                    </>
                  )}
                </Button>
              </form>
            )}
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">Or continue with</span>
              </div>
            </div>
            
            <Button 
              onClick={handleGoogleLogin}
              variant="outline"
              className="w-full h-12 border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-all duration-200"
              data-testid="google-login-button"
            >
              <i className="pi pi-google mr-3 text-lg"></i>
              Continue with Google
            </Button>
            
            <div className="text-center">
              <Button
                variant="link"
                onClick={() => setShowRegister(!showRegister)}
                className="text-custom-orange hover:text-custom-orange"
                data-testid="toggle-auth-mode"
              >
                {showRegister ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
              </Button>
            </div>
            
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                By signing {showRegister ? 'up' : 'in'}, you agree to our Terms of Service
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Utility function to format date/time according to site timezone
const formatDateTime = (utcDateString, siteTimezone = 'Australia/Sydney', options = {}) => {
  if (!utcDateString) return 'N/A';
  try {
    const date = new Date(utcDateString);
    return date.toLocaleString('en-AU', {
      timeZone: siteTimezone,
      ...options
    });
  } catch (error) {
    console.error('Date formatting error:', error);
    return new Date(utcDateString).toLocaleString();
  }
};

const formatDate = (utcDateString, siteTimezone = 'Australia/Sydney') => {
  return formatDateTime(utcDateString, siteTimezone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

const formatTime = (utcDateString, siteTimezone = 'Australia/Sydney') => {
  if (!utcDateString) return 'N/A';
  try {
    const date = new Date(utcDateString);
    return date.toLocaleTimeString('en-AU', {
      timeZone: siteTimezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Time formatting error:', error);
    return new Date(utcDateString).toLocaleTimeString();
  }
};


export default LoginPage;
